import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, CircularProgress, Checkbox, Autocomplete, Tooltip } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import FacilityAddressForm from './FacilityAddressForm';
import ContactListForm from '../../reuseable-components/ContactForm/ContactListForm';
import QuickbooksSearchSelect from '../../reuseable-components/QuickbooksSearchSelect/QuickbooksSearchSelect';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';

FacilityForm.propTypes = {
    editFacility: PropTypes.object,
    setFacilities: PropTypes.func,
    parents: PropTypes.array,
    purchasingGroups: PropTypes.array,
    contactTypes: PropTypes.array,
    open: PropTypes.bool,
    close: PropTypes.func
}

export default function FacilityForm({ editFacility, setFacilities, parents, purchasingGroups, contactTypes, open, close }) {
    const { enqueueSnackbar } = useSnackbar();

    const [submitting, setSubmitting] = useState(false);
    const [loadingFacility, setLoadingFacility] = useState(false);
    const [formValue, setFormValue] = useState({});

    useEffect(() => {
        if (editFacility && open) {
            setLoadingFacility(true);
            axios.get(`/facility/facility?id=${editFacility.id}`)
                .then(res => setFormValue(res.data ?? {}))
                .catch(() => enqueueSnackbar('Get facility data failed', { variant: 'error' }))
                .finally(() => setLoadingFacility(false));
        } else setFormValue({ contacts: [{}] });
    }, [open])

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/facility/facilityForm", objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(`Facility ${(editFacility ? "Updated" : "Created")}`, { variant: 'success' });
          res.data.purchasingGroupName = purchasingGroups.find(purchasingGroup => purchasingGroup.value === res.data.purchasingGroupId)?.label;
          res.data.parentName = parents.find( parent => parent.value === res.data.parentId )?.label;
          setFacilities(facilities => updateItemInPlaceOrAppend(facilities, res.data));
          close();
        }
        else enqueueSnackbar(`${(editFacility ? "Update" : "Create")} facility failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`${(editFacility ? "Update" : "Create")} facility failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

    return <Dialog open={open} onClose={close} maxWidth="md" >
        <DialogContent>
            {!loadingFacility ? <Stack spacing={1} minWidth="600px">
                <Box>
                    <Typography variant="subtitle1">Facility Name*</Typography>
                    <TextField
                        name="name"
                        size="small"
                        placeholder="Enter Facility Name"
                        fullWidth
                        value={formValue.name ?? ""}
                        onChange={(e) => setFormValue(form => ({ ...form, name: e.target.value }))}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle1">Parent</Typography>
                    <Autocomplete
                        fullWidth
                        size="small"
                        value={parents.find(parent => parent.value === formValue.parentId) ?? null}
                        onChange={(event, val) => setFormValue(form => ({ ...form, parentId: val?.value ?? 0 }))}
                        options={parents}
                        renderInput={(params) => <TextField {...params} placeholder="Parent" />}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle1">Facility Code</Typography>
                    <TextField
                        name="facilityCode"
                        size="small"
                        placeholder="Enter Facility Code"
                        fullWidth
                        value={formValue.facilityCode ?? ""}
                        onChange={(e) => setFormValue(form => ({ ...form, facilityCode: e.target.value }))}
                    />
                </Box>
                <QuickbooksSearchSelect formValue={formValue} setFormValue={setFormValue} url="/quickbooks/customerSearch" />
                <Box>
                    <Typography variant="subtitle1">Purchasing Group</Typography>
                    <Autocomplete
                        fullWidth
                        size="small"
                        value={purchasingGroups.find(purchasingGroup => purchasingGroup.value === formValue.purchasingGroupId) ?? null}
                        onChange={(event, val) => setFormValue(form => ({ ...form, purchasingGroupId: val?.value ?? 0 }))}
                        options={purchasingGroups}
                        renderInput={(params) => <TextField {...params} placeholder="Purchasing Group" />}
                    />
                </Box>
                <Box>
                    <Checkbox checked={formValue.useSameAddress ?? false} onChange={(e) => setFormValue(value => ({ ...value, useSameAddress: e.target.checked }))} />
                    Use same address for shipping and billling
                </Box>
                <Box>
                    <Checkbox checked={formValue.onHold ?? false} onChange={(e) => setFormValue(value => ({ ...value, onHold: e.target.checked }))} />
                    Facility on hold
                </Box>

                <FacilityAddressForm setFormValue={setFormValue} formValue={formValue} id={"shippingAddress"} title={!formValue.useSameAddress ? "Shipping Address" : "Address"} />
                {!formValue.useSameAddress && <FacilityAddressForm setFormValue={setFormValue} formValue={formValue} id={"billingAddress"} title={"Billing Address"} />}
                <ContactListForm formValue={formValue} setFormValue={setFormValue} contactTypes={contactTypes} />
                <Box>
                    <LoadingButton loading={submitting} disabled={!formValue?.name} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }}
                        color="purple" size="medium" variant="contained" onClick={onSubmit}>
                        Save
                    </LoadingButton>
                </Box>
            </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
        </DialogContent>
    </Dialog>;
}
